import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { userApi } from '../../api'
import { workoutApi } from '../../api/workoutApi'
import type { WorkoutSession } from '../../api/workoutApi'
import type { CoachStatistics, UserRelation } from '../../types/api'
import { useTranslation } from '../../i18n'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../hooks/useTheme'
import { Avatar } from '../ui'
import { useSubscription } from '../../hooks/useSubscription'
import ActiveWorkoutWidget from '../training/ActiveWorkoutWidget'
import { WORKOUT_FINISHED_EVENT } from '../../utils/workoutEvents'

export default function CoachDashboard() {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const { t, translate } = useTranslation()
  const { user } = useAuth()
  const { usage } = useSubscription()

  const [stats, setStats] = useState<CoachStatistics | null>(null)
  const [trainees, setTrainees] = useState<UserRelation[]>([])
  const [activeWorkout, setActiveWorkout] = useState<WorkoutSession | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [location.pathname])

  useEffect(() => {
    const refreshActiveWorkout = () => {
      workoutApi.getActiveSession()
        .then((active) => setActiveWorkout(active))
        .catch(() => setActiveWorkout(null))
    }
    window.addEventListener(WORKOUT_FINISHED_EVENT, refreshActiveWorkout)
    return () => window.removeEventListener(WORKOUT_FINISHED_EVENT, refreshActiveWorkout)
  }, [])

  async function loadData() {
    setLoading(true)
    try {
      const [statistics, traineeList, workoutSession] = await Promise.all([
        userApi.getUserStatistics(),
        userApi.getTrainees(),
        workoutApi.getActiveSession().catch(() => null),
      ])
      setStats(statistics.coachStats)
      setTrainees(traineeList.filter(tr => tr.relationStatus === 'ACTIVE').slice(0, 6))
      setActiveWorkout(workoutSession)
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const canBeCompleted = stats?.canBeCompleted ?? 0

  return (
    <>
      {/* Over-limit warning */}
      {usage && usage.traineeCount > usage.maxTrainees && (
        <div
          className="mb-4 rounded-xl p-4"
          style={{
            background: 'linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(220,38,38,0.08) 100%)',
            border: '1.5px solid rgba(239,68,68,0.3)',
          }}
        >
          <div className="flex items-start gap-3">
            <div
              className="flex items-center justify-center rounded-full w-10 h-10 flex-shrink-0"
              style={{ backgroundColor: 'rgba(239,68,68,0.15)', border: '2px solid rgba(239,68,68,0.3)' }}
            >
              <span className="text-xl">⚠️</span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-bold">{t.dashboard.overLimitWarning.title}</h3>
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: 'rgba(239,68,68,0.2)', color: 'rgba(239,68,68,1)' }}
                >
                  {usage.traineeCount}/{usage.maxTrainees}
                </span>
              </div>
              <p className="text-xs opacity-80 leading-relaxed mb-3">
                {translate('dashboard.overLimitWarning.traineesExceeded', {
                  current: usage.traineeCount,
                  max: usage.maxTrainees,
                })}
              </p>
              <button
                onClick={() => navigate('/subscription')}
                className="px-4 py-2 rounded-lg text-xs font-semibold transition-all active:scale-95"
                style={{
                  background: `${theme.buttonColor}20`,
                  border: `1.5px solid ${theme.buttonColor}50`,
                  color: theme.textColor,
                }}
              >
                ✨ {t.subscription.unlockCta}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-base opacity-70">{t.common.loading}</div>
      ) : (
        <>
          {activeWorkout?.id != null && (
            <ActiveWorkoutWidget workout={activeWorkout} />
          )}

          {/* Hero summary card */}
          <div
            data-tour="tour-today-overview"
            className="rounded-2xl p-5 mb-4 relative overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
            style={{
              background: 'linear-gradient(135deg, #005f8f 0%, #0088cc 55%, #3ba8dc 100%)',
              boxShadow: '0 8px 32px rgba(0,136,204,0.35)',
            }}
            onClick={() => navigate('/this-week-trainings', { state: { viewMode: 'day' } })}
          >
            {/* Decorative circles */}
            <div className="absolute pointer-events-none" style={{ width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', top: -35, right: -35 }} />
            <div className="absolute pointer-events-none" style={{ width: 70, height: 70, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', bottom: -20, right: 56 }} />

            <div className="relative">
              <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'rgba(255,255,255,0.65)' }}>
                {t.dashboard.hero.todayOverview}
              </p>
              <div className="flex items-end gap-2 mb-3">
                <span className="font-extrabold leading-none" style={{ fontSize: 52, letterSpacing: '-2px', color: 'white' }}>
                  {stats?.todayTrainings ?? 0}
                </span>
                <span className="text-base font-medium pb-2" style={{ color: 'rgba(255,255,255,0.8)' }}>
                  {t.dashboard.hero.sessionsSuffix}
                </span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <span
                  className="text-xs font-semibold px-3 py-1 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}
                >
                  {translate('dashboard.hero.thisWeekChip', { count: stats?.thisWeekTrainings ?? 0 })}
                </span>
                <span
                  className="text-xs font-semibold px-3 py-1 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}
                >
                  {translate('dashboard.hero.activeTraineesChip', { count: stats?.activeTrainees ?? 0 })}
                </span>
              </div>
            </div>
          </div>

          {/* Completable sets alert — only shown when relevant */}
          {canBeCompleted > 0 && (
            <div
              className="rounded-2xl p-3 mb-4 flex items-center gap-3 cursor-pointer active:scale-[0.98] transition-transform"
              style={{
                background: theme.isDark ? 'rgba(255,149,0,0.12)' : 'rgba(255,149,0,0.08)',
                border: '1px solid rgba(255,149,0,0.3)',
              }}
              onClick={() => navigate('/all-training-sets', { state: { initialFilter: 'canBeCompleted' } })}
            >
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-lg"
                style={{ background: 'rgba(255,149,0,0.15)' }}
              >
                ⚡
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold" style={{ color: '#d97706' }}>
                  {canBeCompleted === 1
                    ? translate('dashboard.completableAlert.title_one', { count: canBeCompleted })
                    : translate('dashboard.completableAlert.title_other', { count: canBeCompleted })}
                </div>
                <div className="text-xs opacity-60">{t.dashboard.completableAlert.subtitle}</div>
              </div>
            </div>
          )}

          {/* My trainees strip */}
          <div data-tour="tour-clients" className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-base font-bold" style={{ color: theme.text.primary }}>
              {t.dashboard.traineesSection.title}
            </span>
            <button
              className="text-sm font-semibold"
              style={{ color: theme.buttonColor || theme.accent.text }}
              onClick={() => navigate('/trainees')}
            >
              {t.dashboard.traineesSection.seeAll}
            </button>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {/* Invite tile — dashed-add style, compact square size */}
            <button
              type="button"
              className="flex-shrink-0 flex flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed cursor-pointer active:scale-95 transition-opacity hover:opacity-70"
              style={{
                minWidth: 76,
                height: 90,
                borderColor: theme.cardBorder,
                color: theme.text.muted,
                background: 'transparent',
              }}
              onClick={() => navigate('/invite')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              <span className="text-xs font-semibold" style={{ color: theme.text.muted }}>
                {t.dashboard.traineesSection.invite}
              </span>
            </button>

            {/* Trainee avatar tiles */}
            {trainees.map(trainee => {
              const name = trainee.firstName
                ? `${trainee.firstName}${trainee.lastName ? ' ' + trainee.lastName : ''}`
                : trainee.username ?? '?'
              return (
                <button
                  key={trainee.id}
                  className="flex-shrink-0 flex flex-col items-center justify-center gap-1 rounded-2xl cursor-pointer active:scale-95 transition-transform px-2"
                  style={{
                    minWidth: 76,
                    height: 90,
                    backgroundColor: theme.cardBgLight,
                    border: `1px solid ${theme.cardBorder}`,
                  }}
                  onClick={() => navigate(`/trainee/${trainee.id}`)}
                >
                  <Avatar photoUrl={trainee.photoUrl} name={name} size="sm" fallbackBg={theme.accent.light} />
                  <span
                    className="text-xs font-semibold text-center w-full truncate px-1"
                    style={{ color: theme.text.primary }}
                  >
                    {trainee.firstName ?? trainee.username ?? '?'}
                  </span>
                </button>
              )
            })}
          </div>
          </div>

          {/* My coaches link — conditional */}
          {user?.hasCoaches && (
            <button
              className="w-full mt-3 rounded-2xl p-4 flex items-center gap-3 cursor-pointer active:scale-[0.98] transition-transform"
              style={{ backgroundColor: theme.cardBgLight, border: `1px solid ${theme.cardBorder}` }}
              onClick={() => navigate('/my-coaches')}
            >
              <span className="text-xl flex-shrink-0">👨‍🏫</span>
              <span className="flex-1 text-sm font-semibold text-left" style={{ color: theme.text.primary }}>
                {t.dashboard.myCoaches.title}
              </span>
            </button>
          )}
        </>
      )}
    </>
  )
}
